import { useCallback, useEffect, useState } from 'react';
import cptActivityService, {
    AnalyticsFilters,
    AnalyticsOptions,
    DetailedRow,
} from '../../../services/api/CPTAnalytics/CPTActivityService';
import CPTFiltersSection from './CPTFiltersSection';
import CPTDetailedTable from './CPTDetailedTable';
import { toDateInputValue } from '../../../utils/dateUtils';

const defaultStart = (): string => {
    const d = new Date();
    d.setDate(1);
    return toDateInputValue(d);
};

const defaultEnd = (): string => toDateInputValue(new Date());

export default function CPTDetailedReportSection() {
    const [start, setStart] = useState(defaultStart);
    const [end, setEnd] = useState(defaultEnd);
    const [selectedTechs, setSelectedTechs] = useState<string[]>([]);
    const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);

    const [options, setOptions] = useState<AnalyticsOptions>({
        techNames: [],
        cptCodes: [],
    });
    const [detailedRows, setDetailedRows] = useState<DetailedRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSearch = start.length > 0 && end.length > 0;

    useEffect(() => {
        if (!canSearch) return;

        const fetchOptions = async () => {
            try {
                const data = await cptActivityService.getAnalyticsOptions(
                    start,
                    end
                );
                setOptions(data);
            } catch {
                setOptions({ techNames: [], cptCodes: [] });
            }
        };

        fetchOptions();
    }, [start, end, canSearch]);

    const fetchDetailedAnalytics = useCallback(async () => {
        if (!canSearch) return;

        setLoading(true);
        setError(null);

        const filters: AnalyticsFilters = {
            start,
            end,
            techs: selectedTechs.length > 0 ? selectedTechs : undefined,
            cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
        };

        try {
            const detailed =
                await cptActivityService.getDetailedAnalytics(filters);
            setDetailedRows(detailed);
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } }; message?: string };
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load analytics'
            );
            setDetailedRows([]);
        } finally {
            setLoading(false);
        }
    }, [canSearch, start, end, selectedTechs, selectedCptCodes]);

    return (
        <section>
            <div className="mb-5">
                <h2 className="text-xl font-semibold">
                    Detailed Report
                </h2>
                <p className="text-sm text-muted-foreground">
                    Generate a report of completed cases with CPT codes and estimated revenue. Filter by scan date, user, or code and export as CSV.
                </p>
            </div>

            <div className="space-y-6">
                <CPTFiltersSection
                    start={start}
                    end={end}
                    selectedTechs={selectedTechs}
                    selectedCptCodes={selectedCptCodes}
                    options={options}
                    loading={loading}
                    canSearch={canSearch}
                    detailedRows={detailedRows}
                    onStartChange={setStart}
                    onEndChange={setEnd}
                    onTechsChange={setSelectedTechs}
                    onCptCodesChange={setSelectedCptCodes}
                    onRunAnalytics={fetchDetailedAnalytics}
                />

                {error && (
                    <p className="text-sm text-destructive">{error}</p>
                )}

                <CPTDetailedTable rows={detailedRows} loading={loading} start={start} end={end} />
            </div>
        </section>
    );
}
